// Mahjong main menu. Built from @bn-games/ui-shell primitives rather than the
// shared `createMainMenuScreen` because the trainer's destinations are just
// Play / Rules / Settings / About — no History or Quick-Start buttons that the
// shared six-button component hardcodes (History would route to an unregistered
// scene). Same title/stack/footer structure, mahjong's button set — but mounts
// the same auth widget + back-to-landing link the shared component mounts
// (mountMenuCorners auto-hides inside a Discord Activity), so the corner UI is
// consistent across every game's main menu. Pending invites come from the
// page-lifetime dock (`ui/socialDock.ts`), not from this screen.

import type { MenuScreen } from '../types.js';
import {
  createScreenShell,
  createCrestButton,
  createDivider,
  createMotifWrap,
  mountMenuCorners,
  el,
} from '@bn-games/ui-shell';
import { t } from '../../i18n/I18n.js';
import { APP_VERSION } from '../../utils/constants.js';

export function createMainMenuScreen(): MenuScreen {
  let frame: HTMLElement | null = null;
  let corners: ReturnType<typeof mountMenuCorners> | null = null;

  return {
    mount(root, ctx) {
      const shell = createScreenShell();
      frame = shell.frame;
      const body = shell.body;

      const titleBlock = el('div', { class: 'menu-subtitle' }, [
        el('div', { class: 'main-title', text: t('app.name') }),
        el('div', { class: 'main-title-divider' }, [createDivider()]),
      ]);

      const buttons = el('div', { class: 'button-stack' }, [
        createCrestButton({ label: t('menu.play'), primary: true, onClick: () => ctx.navigate('play') }),
        createCrestButton({ label: t('menu.rules'), onClick: () => ctx.navigate('rules') }),
        createCrestButton({ label: t('menu.settings'), onClick: () => ctx.navigate('settings') }),
        createCrestButton({ label: t('menu.about'), onClick: () => ctx.navigate('about') }),
      ]);

      const footer = el('div', { class: 'footer-motif' }, [
        createMotifWrap(),
        el('div', { class: 'footer-motif__version', text: `v ${APP_VERSION}` }),
      ]);

      const middle = el('div', { class: 'about-center' }, [buttons]);
      body.appendChild(titleBlock);
      body.appendChild(middle);
      body.appendChild(footer);
      root.appendChild(frame);

      corners = mountMenuCorners(root);
    },
    unmount() {
      corners?.destroy();
      corners = null;
      frame?.remove();
      frame = null;
    },
  };
}
